import { useMemo } from 'react';

import { StackedBarChart } from '@repo/videa-components/src/common-charts/index';
import { useAtomValue } from 'jotai';
import { chartColors } from 'node_modules/@repo/videa-components/src/common-charts/themes/colors';

import type { THygieneTaskType } from '@/react-query/insights/types';

import { isCountsAtom, taskTypeAtom } from '../atoms';
import { formatLargeNumber } from '../helpers/formatNumber';
import { taskCodesToTypes } from '../helpers/taskTypeHelpers';

export const OppByTaskTypeChart = ({
  opportunityByTask,
}: {
  opportunityByTask?: (
    | {
        task: string;
        opportunity: number;
        completed: number;
      }
    | undefined
  )[];
}) => {
  const taskType = useAtomValue(taskTypeAtom);
  const taskTypeFromCodes = taskCodesToTypes(taskType);
  const isCounts = useAtomValue(isCountsAtom);

  const filteredOpportunityByTask = useMemo(() => {
    return (
      opportunityByTask?.filter(
        (
          task,
        ): task is { task: string; opportunity: number; completed: number } =>
          !!task &&
          (taskTypeFromCodes.length === 0 ||
            taskTypeFromCodes.includes(task.task as THygieneTaskType)),
      ) ?? []
    );
  }, [opportunityByTask, taskTypeFromCodes]);

  return (
    <div className="vai-bg-white vai-rounded-lg vai-p-4 vai-border-gray-200 vai-border-[1px] vai-w-[60%]">
      <div className="vai-flex vai-flex-col">
        <span className="vai-typography-h4">Task Completion by Type</span>
        <span className="vai-typography-muted">
          Which tasks have lower completion rates and attention?
        </span>
      </div>
      {filteredOpportunityByTask.length > 0 ? (
        <StackedBarChart
          data={[
            {
              name: 'Uncompleted Tasks',
              data: filteredOpportunityByTask.map((task) => task.opportunity),
            },
            {
              name: 'Completed Tasks',
              data: filteredOpportunityByTask.map((task) => task.completed),
            },
          ]}
          chartOptions={{
            colors: [chartColors['chart-tint'][2], chartColors.chart[2]],
            unselect: false,
            xAxisCategories: filteredOpportunityByTask.map((task) => task.task),
            yAxisFormatter: function (this: any) {
              return formatLargeNumber(
                Math.abs(this.value),
                isCounts ? '' : '$',
              );
            },
            dataLabelsFormatter: function (this: any) {
              if (this.shapeArgs.height < 65) {
                return '';
              }

              return `<span
                  style=color:${
                    this.series.userOptions.name === 'Uncompleted Tasks'
                      ? 'black'
                      : 'white'
                  }

                >
                  ${formatLargeNumber(this.y, isCounts ? '' : '$')}
                </span>`;
            },
            tooltipFormatter: function (this: any) {
              return `${this.series.userOptions.name}: ${formatLargeNumber(
                Math.abs(this.y),
                isCounts ? '' : '$',
              )}`;
            },
          }}
        />
      ) : (
        <div className="vai-flex vai-p-4">
          <img
            src="/assets/images/insights/oppByTxLoading.png"
            alt="loading"
            className="vai-w-full vai-opacity-20"
          />
        </div>
      )}
    </div>
  );
};
